const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_QUALITY = 0.82;
const SKIP_BELOW_BYTES = 400_000;

export class ImageDownscaleError extends Error {
  constructor(message = 'IMAGE_DOWNSCALE_FAILED') {
    super(message);
    this.name = 'ImageDownscaleError';
  }
}

export function isImageDownscaleError(error: unknown): boolean {
  return error instanceof ImageDownscaleError;
}

function scaleToFit(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
) {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Single-pass downscale decode. Prefer resizeWidth only so aspect ratio is kept
 * without a separate probe decode.
 */
async function createDownscaledBitmap(
  file: File,
  maxWidth: number,
  maxHeight: number
): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, {
      resizeWidth: maxWidth,
      resizeQuality: 'medium',
    });
  } catch {
    try {
      return await createImageBitmap(file, {
        resizeHeight: maxHeight,
        resizeQuality: 'medium',
      });
    } catch {
      throw new Error('createImageBitmap resize unsupported');
    }
  }
}

export type CompressImageForUploadOptions = {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
};

/**
 * Downscales and re-encodes camera photos before upload to reduce payload size
 * and main-thread/network pressure on mobile.
 */
export async function compressImageForUpload(
  file: File,
  options: CompressImageForUploadOptions = {}
): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  const maxWidth = options.maxWidth ?? DEFAULT_MAX_DIMENSION;
  const maxHeight = options.maxHeight ?? DEFAULT_MAX_DIMENSION;
  const quality = options.quality ?? DEFAULT_QUALITY;

  if (file.size <= SKIP_BELOW_BYTES && file.type === 'image/jpeg') {
    return file;
  }

  let bitmap: ImageBitmap | undefined;
  let canvas: HTMLCanvasElement | undefined;

  try {
    bitmap = await createDownscaledBitmap(file, maxWidth, maxHeight);
    const { width, height } = scaleToFit(
      bitmap.width,
      bitmap.height,
      maxWidth,
      maxHeight
    );

    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas!.toBlob(resolve, 'image/jpeg', quality);
    });

    // Already well-compressed (or encode failed) — keep the original.
    if (!blob || blob.size >= file.size) {
      return file;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    bitmap?.close();
    // Help the GC reclaim the canvas backing store on constrained WebViews.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
